import type { MonthlyTableCashFlowMonth } from 'features/drilldown/cashflows/gql/__generated__/cashflowDataFragments';
export type MergedRemittanceCashflows = {
  /** Combined past + projected rows, with `month` reindexed 1..(P+K). */
  monthlyCashFlows: MonthlyTableCashFlowMonth[];
  /** Number of past-month rows at the head of the array. Lets the chart render a
   *  "past / projected" divider or different styling per segment if desired. */
  pastMonthCount: number;
};

/**
 * Concatenates past-month remittance summaries with forward-projected cashflows for the
 * RemittanceCashFlow page. Past rows keep their input order (ascending payment month);
 * projected rows are reindexed to continue from where the past rows end so the chart
 * x-axis reads as one continuous timeline.
 *
 * Pure — no side effects, unit-testable.
 */
export function mergePastAndProjected(
  past: MonthlyTableCashFlowMonth[],
  projected: MonthlyTableCashFlowMonth[],
): MergedRemittanceCashflows {
  // Past rows already carry month = 1..P from summariesByPaymentMonthToMonthlyTable. Use
  // their existing month values as-is and continue the projection from (last past month + 1).
  // When past is empty, projection starts at month 1 (so a deal with zero history still shows
  // a projection that reads naturally).
  const pastCount = past.length;
  const projectedStart = pastCount > 0 ? pastCount + 1 : 1;
  const projectedReindexed = projected.map((row, idx) => ({
    ...row,
    month: projectedStart + idx,
  }));
  return {
    monthlyCashFlows: [...past, ...projectedReindexed],
    pastMonthCount: pastCount,
  };
}
